/**
 * 表示言語の Context (admin-english-toggle-20260924 / ADR-081)。
 *
 * - 管理画面の shell (`AppLayout`) の中だけで使う。Provider の外 (依頼者ポータル・ログイン画面・
 *   単体テスト) では既定値 = 日本語で描画され、切替は何もしない。
 * - 表示言語は利用者ごとに localStorage へ保存する (`localeStorage.ts`)。
 * - サーバー個人設定との同期は呼び出し側 (`AdminLocaleProvider`) が関数で渡す。この部品は
 *   API を知らない (テストで差し替えやすくし、i18n の中心から通信を切り離す)。
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { readStoredLocale, writeStoredLocale } from './localeStorage';
import { translate, type HandleTFunction } from './translate';
import { DEFAULT_LOCALE, type Locale, type NamespaceHandle } from './types';

/** 表示言語を切り替えるときの指定。 */
export interface SetLocaleOptions {
  /**
   * サーバー個人設定にも保存するか (既定 true)。個人設定画面のように、呼び出し側が
   * 自分で保存する場合は false にして二重保存を避ける。
   */
  persistRemote?: boolean;
}

export interface LocaleContextValue {
  /** 現在の表示言語 */
  locale: Locale;
  /** 表示言語を切り替える (端末に保存し、既定ではサーバー個人設定にも保存する) */
  setLocale: (next: Locale, options?: SetLocaleOptions) => void;
}

const DEFAULT_CONTEXT: LocaleContextValue = {
  locale: DEFAULT_LOCALE,
  // Provider の外 (依頼者ポータル等) では切り替えない
  setLocale: () => undefined,
};

const LocaleContext = createContext<LocaleContextValue>(DEFAULT_CONTEXT);

export interface LocaleProviderProps {
  children: ReactNode;
  /** 端末内保存を分ける単位 (利用者 ID)。 */
  storageScope: string | null | undefined;
  /**
   * 端末に保存が無いときだけ 1 回呼ぶ (サーバー個人設定の表示言語)。null / 失敗は日本語のまま。
   * 別の PC で英語を選んだ職員が、新しいブラウザでも英語で始められるようにする。
   */
  loadRemoteLocale?: () => Promise<Locale | null>;
  /** 利用者が切り替えたときに呼ぶ (サーバー個人設定への保存)。 */
  saveRemoteLocale?: (locale: Locale) => Promise<void>;
  /** サーバーへの保存に失敗したとき (切替自体は端末内で有効なまま)。 */
  onRemoteSaveError?: (locale: Locale, error: unknown) => void;
  /**
   * 開始時の言語を端末内保存より優先して決める (画面単位のテストで英語表示を描くとき用)。
   * 本番の shell (`AdminLocaleProvider`) は渡さない。
   */
  initialLocale?: Locale;
}

/** 利用者ごとの記録に使うキー (未ログイン等で scope が無いときは空文字)。 */
function scopeKey(scope: string | null | undefined): string {
  return scope ?? '';
}

/** 利用者の開始時の言語 (テスト用の指定 → 端末内保存 → 日本語)。 */
function startingLocale(scope: string | null | undefined, initialLocale?: Locale): Locale {
  return initialLocale ?? readStoredLocale(scope) ?? DEFAULT_LOCALE;
}

export function LocaleProvider({
  children,
  storageScope,
  loadRemoteLocale,
  saveRemoteLocale,
  onRemoteSaveError,
  initialLocale,
}: LocaleProviderProps) {
  // 表示言語は「どの利用者の設定か」と組で持つ。利用者が変わったら (別の職員でログインし直した等)、
  // その利用者の端末内保存から始め直し、前の利用者の選択 (英語) を引き継がない。
  const [state, setState] = useState(() => ({
    scope: storageScope,
    locale: startingLocale(storageScope, initialLocale),
  }));
  let locale = state.locale;
  if (state.scope !== storageScope) {
    // props (利用者) の変化で state を作り直す。描画中に直すので、前の利用者の言語は一度も描かれない。
    locale = startingLocale(storageScope, initialLocale);
    setState({ scope: storageScope, locale });
  }
  /**
   * 自分で切り替えた利用者。その利用者については、後から届いたサーバーの値で選択を上書きしない
   * (利用者ごとに持つ — 前の利用者が切り替えたことで、次の利用者のサーバーの値を捨てない)。
   */
  const userChangedScopesRef = useRef(new Set<string>());

  // 端末に保存が無いときだけサーバー個人設定を読み、採用する (端末にも保存する)。
  useEffect(() => {
    if (!loadRemoteLocale || initialLocale) return;
    if (readStoredLocale(storageScope) !== null) return;
    let cancelled = false;
    loadRemoteLocale()
      .then((remote) => {
        if (cancelled || remote === null) return;
        if (userChangedScopesRef.current.has(scopeKey(storageScope))) return;
        setState({ scope: storageScope, locale: remote });
        writeStoredLocale(storageScope, remote);
      })
      .catch(() => {
        // 読めなければ既定の日本語のまま使える (業務は止めない)。次回の起動で再び読む。
      });
    return () => {
      cancelled = true;
    };
  }, [loadRemoteLocale, storageScope, initialLocale]);

  // 支援技術・ブラウザ翻訳のため <html lang> を表示言語に合わせ、shell を外れたら元に戻す。
  useEffect(() => {
    const root = document.documentElement;
    const previous = root.getAttribute('lang');
    root.setAttribute('lang', locale);
    return () => {
      if (previous === null) root.removeAttribute('lang');
      else root.setAttribute('lang', previous);
    };
  }, [locale]);

  const setLocale = useCallback(
    (next: Locale, options?: SetLocaleOptions) => {
      userChangedScopesRef.current.add(scopeKey(storageScope));
      setState({ scope: storageScope, locale: next });
      writeStoredLocale(storageScope, next);
      const persistRemote = options?.persistRemote ?? true;
      if (persistRemote && saveRemoteLocale) {
        saveRemoteLocale(next).catch((error: unknown) => onRemoteSaveError?.(next, error));
      }
    },
    [storageScope, saveRemoteLocale, onRemoteSaveError],
  );

  const value = useMemo<LocaleContextValue>(() => ({ locale, setLocale }), [locale, setLocale]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

/** 現在の表示言語と切替関数。Provider の外では日本語 (切替なし)。 */
export function useLocale(): LocaleContextValue {
  return useContext(LocaleContext);
}

/**
 * 名前空間を固定した翻訳関数。キーと差し込み値は型で検査される
 * (`{name}` の無い文言に値を渡す / ある文言で値の不足・綴り違い・余分 → コンパイルエラー)。
 * 動的なキーを受けて差し込み値を素通しする汎用の小道具は、`translate` を直接使う。
 */
export type TFunction<H extends NamespaceHandle> = HandleTFunction<H>;

/**
 * 画面の文言を引く hook。`const t = useT(tasksNs); t('page.title')`。
 * handle は `i18n/namespaces/{ns}.ts` から import する (辞書はその画面の chunk に入る)。
 * 選んだ言語に無い文言は日本語で表示する (`translate` の fallback)。
 */
export function useT<H extends NamespaceHandle>(handle: H): TFunction<H> {
  const { locale } = useLocale();
  const name = handle.name;
  return useCallback<TFunction<H>>(
    (key, ...params) => translate(locale, `${name}.${key}`, params[0]),
    [locale, name],
  );
}
